import { cloneElement, isValidElement, useId, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useAnchoredPosition } from "../hooks";
import { cx } from "../util";
import { GfPortal } from "./Portal";

export interface TooltipProps {
  content: ReactNode;
  /** A single focusable element (button, link…). */
  children: ReactElement;
  side?: "top" | "bottom";
  align?: "start" | "center" | "end";
  /** ms before showing on hover. */
  delay?: number;
  /** Add aria-describedby to the child (off when the tooltip just repeats the aria-label). */
  describeChild?: boolean;
  disabled?: boolean;
  className?: string;
}

export function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  delay = 350,
  describeChild = true,
  disabled,
  className,
}: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  useAnchoredPosition(anchorRef, tipRef, open, { side, align, offset: 8 });

  function show(immediate: boolean) {
    if (disabled || content === null || content === undefined || content === "") return;
    window.clearTimeout(timer.current);
    if (immediate) setOpen(true);
    else timer.current = window.setTimeout(() => setOpen(true), delay);
  }
  function hide() {
    window.clearTimeout(timer.current);
    setOpen(false);
  }

  const child =
    describeChild && open && isValidElement<{ "aria-describedby"?: string }>(children)
      ? cloneElement(children, { "aria-describedby": id })
      : children;

  return (
    <span
      ref={anchorRef}
      className="gf-tooltip-anchor"
      onMouseEnter={() => show(false)}
      onMouseLeave={hide}
      onFocus={(e) => {
        // Only keyboard focus shows instantly; mouse focus follows hover rules.
        if ((e.target as HTMLElement).matches?.(":focus-visible")) show(true);
      }}
      onBlur={hide}
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) hide();
      }}
    >
      {child}
      {open ? (
        <GfPortal>
          <div ref={tipRef} id={id} role="tooltip" className={cx("gf-tooltip", className)} style={{ top: -9999, left: -9999 }}>
            {content}
          </div>
        </GfPortal>
      ) : null}
    </span>
  );
}
